import axios from "axios";
import { apiBaseUrl } from "@/utils/BaseUrl.js";
import { getLocationId } from "../utils/SessionDataHandler";
import LoggerService from "@/services/LoggerService";

const api = axios.create({
  baseURL: apiBaseUrl,
  // withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ✅ Request Interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // config.withCredentials = true
    
    // if (config.url) {
    //   config.url = config.url.toLowerCase();
    // }
    
    // Ensure locationid is included in managelocationajax requests
    // This prevents backend errors when session is null on local servers
    if (config.url && config.url.includes("managelocationajax")) {
      const locationId = getLocationId();
      if (locationId && config.data) {
        // If locationid is not in the payload, add it
        if (!config.data.locationid && !config.data.locationId) {
          config.data.locationid = locationId;
        }
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ Response Interceptor (Global Validation Handling)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error("API Error:", error);

    // Log API error (excluding 401 to avoid spam during logout)
    if (error.response?.status !== 401) {
      try {
        await LoggerService.logAPIError(
          error.config?.url || 'unknown',
          error.config?.method?.toUpperCase() || 'UNKNOWN',
          error,
          error.config?.data
        );
      } catch (logError) {
        // Silently fail logging to prevent infinite loops
        console.error("Failed to log API error:", logError);
      }
    }

    // Handle unauthorized error (401) - Session expired
    if (error.response && error.response.status === 401) {
      // Clear session data
      localStorage.removeItem("token");
      localStorage.removeItem("sessionData");
      
      // Clear any other auth-related storage
      sessionStorage.clear();
      
      // Public/embeddable pages should not redirect to sign-in on 401.
      // A stale token was cleared above; the page can continue as guest.
      // Match the segment anywhere in the path so this still works when the
      // app is served under a base path (e.g. /admin-dashboard/all-schedules/).
      const publicPrefixes = ["/all-schedules/"];
      const currentPath = window.location.pathname;
      const isPublicPage = publicPrefixes.some(p => currentPath.includes(p));
      const isAuthPage = currentPath.includes("/sign-in") || currentPath.includes("/sign-up");

      if (!isPublicPage && !isAuthPage) {
        // Check if router is available via window (set in main.js or App.vue)
        if (window.__router__) {
          window.__router__.push({ name: "SignIn" });
        } else {
          // Fallback to window.location for full page reload
          window.location.href = "/sign-in";
        }
      }
      
      return Promise.reject({
        type: "unauthorized",
        message: "Session expired. Please login again.",
        original: error,
      });
    }

    // Handle validation error (422)
    if (error.response && error.response.status === 422) {
      const responseData = error.response.data || {};
      const messages = [];

      // Try to extract validation messages from different possible formats
      // Format 1: details object with field names as keys
      if (responseData.details && typeof responseData.details === 'object') {
        Object.keys(responseData.details).forEach((field) => {
          const fieldErrors = Array.isArray(responseData.details[field]) 
            ? responseData.details[field] 
            : [responseData.details[field]];
          fieldErrors.forEach((msg) => {
            if (msg) {
              messages.push(`${field}: ${msg}`);
            }
          });
        });
      }

      // Format 2: errors array
      if (responseData.errors && Array.isArray(responseData.errors)) {
        responseData.errors.forEach((err) => {
          if (typeof err === 'string') {
            messages.push(err);
          } else if (err.field && err.message) {
            messages.push(`${err.field}: ${err.message}`);
          } else if (err.message) {
            messages.push(err.message);
          }
        });
      }

      // Format 3: message field (fallback)
      if (messages.length === 0 && responseData.message) {
        messages.push(responseData.message);
      }

      // Format 4: Check if original error has message
      if (messages.length === 0 && error.message) {
        messages.push(error.message);
      }

      // Default message if nothing found
      if (messages.length === 0) {
        messages.push("Validation failed. Please check your input.");
      }

      // ✅ Show toast notification globally if Vue app is available
      if (window.__app__ && window.__app__.$toast) {
        window.__app__.$toast.error(messages.join("\n"), {
          position: "top-right",
        });
      }

      // Return structured validation error
      return Promise.reject({
        type: "validation",
        messages,
        original: error,
      });
    }

    // Handle 500 error related to locationid being null in session
    // This happens when backend session is not initialized on local servers
    if (
      error.response &&
      error.response.status === 500 &&
      error.config &&
      error.config.url &&
      error.config.url.includes("managelocationajax")
    ) {
      const errorMessage = error.response?.data?.message || "";
      const isLocationIdError =
        errorMessage.includes("locationid") ||
        errorMessage.includes("location") ||
        errorMessage.includes("Attempt to read property");

      if (isLocationIdError) {
        const locationId = getLocationId();
        if (locationId) {
          try {
            // Try to set the location in backend session first
            await axios.post(
              `${apiBaseUrl}/managelocationajax`.toLowerCase(),
              {
                cmd: "setlocation",
                locationid: locationId,
              },
              {
                headers: {
                  Authorization: error.config.headers?.Authorization,
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                // withCredentials: true,
              }
            );

            // Retry the original request
            console.log("Retrying request after setting location in session");
            return api.request(error.config);
          } catch (retryError) {
            console.error("Failed to set location in session:", retryError);
            // Continue with original error handling
          }
        }
      }
    }

    // Handle other errors (network, 500, etc.)
    const message =
      error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred";

    if (window.__app__ && window.__app__.$toast) {
      window.__app__.$toast.error(message, { position: "top-right" });
    }

    return Promise.reject(error);
  }
);

export default api;
